import { useEffect, useState } from "react";

import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import toast from "react-hot-toast";

import DashboardLayout from "../../../components/layout/DashboardLayout";

import { useWeightTestViewModel } from "../../../viewmodels/operation/weighttest/useWeightTestViewModel";

const EditWeightTest = () => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const page = searchParams.get("page") ?? "1";
  const search = searchParams.get("search") ?? "";

  const { id } = useParams();

  const { getWeightTestById, updateWeightTest } = useWeightTestViewModel();

  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState<any>({
    customerName: "",
    customerAddress: "",

    signature: "",
    signedBy: "",
    designation: "",

    date: "",
    dueDate: "",

    formNo: "",

    isPrinted: 0,
  });

  const [items, setItems] = useState<any[]>([]);

  // ================= LOAD =================
  const loadData = async () => {
    if (!id) return;

    const data = await getWeightTestById(id);

    if (data) {
      setForm({
        id: data.id,

        customerName: data.customerName || "",

        customerAddress: data.customerAddress || "",

        signature: data.signature || "",

        signedBy: data.signedBy || "",

        designation: data.designation || "",

        date: data.date?.slice(0, 10) || "",

        dueDate: data.dueDate?.slice(0, 10) || "",

        formNo: data.formNo || "",

        isPrinted: data.isPrinted || 0,
      });

      setItems(data.weightTestLineItems || []);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [id]);

  // ================= ADD ROW =================
  const addRow = () => {
    setItems([
      ...items,

      {
        capacity: "",
        capacityUnit: "",

        actualReading: "",
        actualReadingUnit: "",

        weightBox: "",
      },
    ]);
  };

  // ================= REMOVE ROW =================
  const removeRow = (index: number) => {
    const updated = [...items];

    updated.splice(index, 1);

    setItems(updated);
  };

  // ================= SAVE =================
  const handleSubmit = async () => {
    if (!form.customerName) {
      toast.error("Customer name required");

      return;
    }

    const payload = {
      id: form.id,

      customerName: form.customerName,

      customerAddress: form.customerAddress,

      signature: form.signature,

      signedBy: form.signedBy,

      designation: form.designation,

      date: form.date ? new Date(form.date).toISOString() : null,

      dueDate: form.dueDate ? new Date(form.dueDate).toISOString() : null,

      formNo: Number(form.formNo),

      isPrinted: Number(form.isPrinted),

      weightTestLineItems: items.map((x) => ({
        id: x.id,

        capacity: Number(x.capacity),

        capacityUnit: x.capacityUnit,

        actualReading: Number(x.actualReading),

        actualReadingUnit: x.actualReadingUnit,

        weightBox: x.weightBox,
      })),
    };

    const success = await updateWeightTest(payload);

    if (success) {
      navigate(
        `/weight-test-reports?page=${page}&search=${encodeURIComponent(search)}`,
      );
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-10">Loading...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Edit Weight Test</h1>

        <div className="space-y-8">
          {/* ================= CUSTOMER ================= */}
          <div className="bg-white border rounded-3xl p-6 space-y-6">
            <div className="border-b pb-4">
              <h2 className="text-2xl font-bold">Customer Details</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block mb-2 font-medium">Customer Name</label>

                <input
                  value={form.customerName}
                  onChange={(e) =>
                    setForm({
                      ...form,

                      customerName: e.target.value,
                    })
                  }
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>

              <div>
                <label className="block mb-2 font-medium">
                  Customer Address
                </label>

                <input
                  value={form.customerAddress}
                  onChange={(e) =>
                    setForm({
                      ...form,

                      customerAddress: e.target.value,
                    })
                  }
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>

              <div>
                <label className="block mb-2 font-medium">Date</label>

                <input
                  type="date"
                  value={form.date}
                  onChange={(e) =>
                    setForm({
                      ...form,

                      date: e.target.value,
                    })
                  }
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>

              <div>
                <label className="block mb-2 font-medium">Due Date</label>

                <input
                  type="date"
                  value={form.dueDate}
                  onChange={(e) =>
                    setForm({
                      ...form,

                      dueDate: e.target.value,
                    })
                  }
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>
            </div>
          </div>

          {/* ================= TABLE ================= */}
          <div className="bg-white border rounded-3xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Weight Test Items</h2>

              <button
                onClick={addRow}
                className="px-5 py-3 bg-primary text-white rounded-2xl"
              >
                + Add Row
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="border p-3">Capacity</th>

                    <th className="border p-3">Capacity Unit</th>

                    <th className="border p-3">S/No.</th>

                    <th className="border p-3">Actual Reading</th>

                    <th className="border p-3">Actual Reading Unit</th>

                    <th className="border p-3">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {items.map((item, index) => (
                    <tr key={index}>
                      <td className="border p-2">
                        <input
                          value={item.capacity}
                          onChange={(e) => {
                            const updated = [...items];

                            updated[index].capacity = e.target.value;

                            setItems(updated);
                          }}
                          className="w-full border rounded-lg px-3 py-2"
                        />
                      </td>

                      <td className="border p-2">
                        <select
                          value={item.capacityUnit || ""}
                          onChange={(e) => {
                            const updated = [...items];

                            updated[index].capacityUnit = e.target.value;

                            setItems(updated);
                          }}
                          className="w-full border rounded-lg px-3 py-2 bg-white"
                        >
                          <option value="">Select</option>

                          <option value="t">t</option>

                          <option value="kg">kg</option>

                          <option value="g">g</option>

                          <option value="mg">mg</option>

                          

                    <option value="lb">lb</option>

                    <option value="oz">oz</option>
                        </select>
                      </td>

                      <td className="border p-2">
                        <input
                          value={item.weightBox}
                          onChange={(e) => {
                            const updated = [...items];

                            updated[index].weightBox = e.target.value;

                            setItems(updated);
                          }}
                          className="w-full border rounded-lg px-3 py-2"
                        />
                      </td>

                      <td className="border p-2">
                        <input
                          value={item.actualReading}
                          onChange={(e) => {
                            const updated = [...items];

                            updated[index].actualReading = e.target.value;

                            setItems(updated);
                          }}
                          className="w-full border rounded-lg px-3 py-2"
                        />
                      </td>

                      <td className="border p-2">
                        <select
                          value={item.actualReadingUnit || ""}
                          onChange={(e) => {
                            const updated = [...items];

                            updated[index].actualReadingUnit = e.target.value;

                            setItems(updated);
                          }}
                          className="w-full border rounded-lg px-3 py-2 bg-white"
                        >
                          <option value="">Select</option>

                          <option value="t">t</option>

                          <option value="kg">kg</option>

                          <option value="g">g</option>

                          <option value="mg">mg</option>
                          

                    <option value="lb">lb</option>

                    <option value="oz">oz</option>
                        </select>
                      </td>

                      <td className="border p-2">
                        <button
                          onClick={() => removeRow(index)}
                          className="px-3 py-2 bg-red-100 text-red-700 rounded-xl"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ================= FOOTER ================= */}
          <div className="bg-white border rounded-3xl p-6">
            <div className="border-b pb-4 mb-6">
              <h2 className="text-2xl font-bold">Signature Details</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {/* <div>

            <label className="block mb-2 font-medium">
              Signature
            </label>

            <input
              value={
                form.signature
              }
              onChange={(e) =>
                setForm({

                  ...form,

                  signature:
                    e.target.value,

                })
              }
              className="w-full border rounded-xl px-4 py-3"
            />

          </div> */}

              <div>
                <label className="block mb-2 font-medium">Signed By</label>

                <input
                  value={form.signedBy}
                  onChange={(e) =>
                    setForm({
                      ...form,

                      signedBy: e.target.value,
                    })
                  }
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>

              <div>
                <label className="block mb-2 font-medium">Designation</label>

                <input
                  value={form.designation}
                  onChange={(e) =>
                    setForm({
                      ...form,

                      designation: e.target.value,
                    })
                  }
                  className="w-full border rounded-xl px-4 py-3"
                />
              </div>
            </div>
          </div>

          {/* BUTTON */}
          <div className="flex justify-end">
            <button
              onClick={handleSubmit}
              className="bg-primary text-white px-8 py-4 rounded-2xl font-semibold"
            >
              Update Weight Test
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EditWeightTest;
